import { type MessageKey } from '@/i18n/messages'
import { placedMessageId } from '@shared/tool-results'
import { type Mention } from './mentions'

// What the AI did to the board in a turn, for people: each change with the
// intent the AI gave it and the things it touched (DESIGN.md principle 5:
// trace, folded to one line; the full calls stay in the trace panel).

export type Action = {
  id: string
  tool: string
  input: Record<string, unknown>
  /** null while the call is still being written or run. */
  result: { content: string; error: boolean } | null
}

export type Verb =
  | 'created'
  | 'edited'
  | 'rewrote'
  | 'addedLink'
  | 'moved'
  | 'deleted'
  | 'connected'
  | 'relabelled'
  | 'unlinked'
  | 'grouped'
  | 'regrouped'
  | 'read'
  | 'readMaterial'
  | 'viewedPage'
  | 'searched'
  | 'clipped'
  | 'adjustedClip'
  | 'quoted'
  | 'addedFile'
  | 'viewedItem'
  | 'posted'
  | 'other'

export type ActionView = {
  id: string
  verb: Verb
  /** The AI's own words for what this is for, when it gave them. */
  intent: string | null
  /** What it touched, as pointers; empty when the things are gone (a deletion) or not known yet. */
  targets: Mention[]
  /** How many things, for changes whose targets cannot be shown (a deletion). */
  count: number
  /** Something to show when there is no target to point at: a card's title being written, a URL. */
  hint: string | null
  failed: boolean
  running: boolean
}

const str = (v: unknown) => (typeof v === 'string' ? v : null)
const strs = (v: unknown) =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
const created = (result: Action['result'], kind: string) =>
  result && !result.error ? (new RegExp(`${kind} (\\w+)`).exec(result.content)?.[1] ?? null) : null
const item = (id: string | null): Mention[] => (id ? [{ kind: 'item', id }] : [])

/** First line of markdown without the heading marks: a card's title. */
export function titleOf(markdown: string): string {
  return (markdown.split('\n')[0] ?? '').replace(/^#+\s*/, '').trim()
}

function host(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/** Board-facing description of one call, or null for a `say` that stayed in the chat. */
export function viewAction(a: Action): ActionView | null {
  const i = a.input
  const base = {
    id: a.id,
    intent: str(i.intent)?.trim() || null,
    targets: [] as Mention[],
    count: 1,
    hint: null as string | null,
    failed: !!a.result?.error,
    running: !a.result,
  }
  switch (a.tool) {
    case 'say': {
      if (!i.place && !i.reply_to) return null
      const placed = a.result && !a.result.error ? placedMessageId(a.result.content) : null
      return { ...base, verb: 'posted', targets: item(placed) }
    }
    case 'create_card': {
      const id = created(a.result, 'card')
      return {
        ...base,
        verb: 'created',
        targets: item(id),
        hint: titleOf(str(i.markdown) ?? '') || null,
      }
    }
    case 'edit_card':
      return { ...base, verb: 'edited', targets: item(str(i.id)) }
    case 'write_card':
      return { ...base, verb: 'rewrote', targets: item(str(i.id)) }
    case 'add_link':
      return {
        ...base,
        verb: 'addedLink',
        targets: item(created(a.result, 'link')),
        hint: str(i.url),
      }
    case 'move': {
      const ids = Array.isArray(i.moves)
        ? i.moves.flatMap((m) =>
            m && typeof m === 'object' && typeof (m as { id?: unknown }).id === 'string'
              ? [(m as { id: string }).id]
              : [],
          )
        : []
      return {
        ...base,
        verb: 'moved',
        targets: ids.map((id) => ({ kind: 'item', id })),
        count: ids.length,
      }
    }
    case 'delete': {
      const ids = strs(i.ids)
      return { ...base, verb: 'deleted', count: ids.length }
    }
    case 'connect': {
      const edge = created(a.result, 'edge')
      return {
        ...base,
        verb: 'connected',
        targets: edge ? [{ kind: 'edge', id: edge }] : [...item(str(i.from)), ...item(str(i.to))],
      }
    }
    case 'update_connection': {
      const id = str(i.id)
      return {
        ...base,
        verb: i.remove ? 'unlinked' : 'relabelled',
        targets: id && !i.remove ? [{ kind: 'edge', id }] : [],
      }
    }
    case 'create_group':
      return {
        ...base,
        verb: 'grouped',
        targets: item(created(a.result, 'group')),
        hint: str(i.title),
      }
    case 'update_group':
      return { ...base, verb: 'regrouped', targets: item(str(i.id)) }
    case 'fetch_url':
      return { ...base, verb: 'read', hint: str(i.url) && host(str(i.url) ?? '') }
    case 'read_material':
      return { ...base, verb: 'readMaterial', hint: str(i.pages) && `p.${str(i.pages)}` }
    case 'view_page':
      return { ...base, verb: 'viewedPage', count: typeof i.page === 'number' ? i.page : 0 }
    case 'search':
      return { ...base, verb: 'searched', hint: str(i.query) }
    case 'clip': {
      const ids =
        a.result && !a.result.error
          ? [...a.result.content.matchAll(/^clip (\w+) /gm)].map((m) => m[1] ?? '')
          : []
      const n = Array.isArray(i.clips) ? i.clips.length : 1
      return {
        ...base,
        verb: 'clipped',
        targets: ids.map((id): Mention => ({ kind: 'item', id })),
        count: n,
      }
    }
    case 'quote': {
      const id =
        a.result && !a.result.error ? (/clip (\w+)/.exec(a.result.content)?.[1] ?? null) : null
      return { ...base, verb: 'quoted', targets: item(id), hint: str(i.text)?.slice(0, 24) ?? null }
    }
    case 'adjust_clip':
      return { ...base, verb: 'adjustedClip', targets: item(str(i.id)) }
    case 'add_file': {
      // A PDF is placed as its handle, an image or anything else as itself.
      const id =
        a.result && !a.result.error
          ? (/(?:placed (?:image|file)|its handle) (\w+)/.exec(a.result.content)?.[1] ?? null)
          : null
      const url = str(i.url)
      return { ...base, verb: 'addedFile', targets: item(id), hint: id ? null : url && host(url) }
    }
    case 'view_item':
      return { ...base, verb: 'viewedItem', targets: item(str(i.id)) }
    default:
      return { ...base, verb: 'other', hint: a.tool }
  }
}

export function viewActions(actions: Action[]): ActionView[] {
  // Something made in this turn is simply where it ended up: moving it
  // afterwards is part of making it, not a change of its own.
  const made = new Set<string>()
  return actions.flatMap((a) => {
    const v = viewAction(a)
    if (!v) return []
    if (v.verb === 'moved' && !v.running && !v.failed) {
      const targets = v.targets.filter((m) => !(m.kind === 'item' && made.has(m.id)))
      if (!targets.length) return []
      return [{ ...v, targets, count: targets.length }]
    }
    if (
      v.verb === 'created' ||
      v.verb === 'addedLink' ||
      v.verb === 'grouped' ||
      v.verb === 'clipped' ||
      v.verb === 'quoted' ||
      v.verb === 'addedFile'
    )
      for (const m of v.targets) if (m.kind === 'item') made.add(m.id)
    return [v]
  })
}

/** How each kind of change reads to a person. */
export const VERB: Record<ActionView['verb'], MessageKey> = {
  created: 'act.created',
  edited: 'act.edited',
  rewrote: 'act.rewrote',
  addedLink: 'act.addedLink',
  moved: 'act.moved',
  deleted: 'act.deleted',
  connected: 'act.connected',
  relabelled: 'act.relabelled',
  unlinked: 'act.unlinked',
  grouped: 'act.grouped',
  regrouped: 'act.regrouped',
  read: 'act.read',
  readMaterial: 'act.readMaterial',
  viewedPage: 'act.viewedPage',
  searched: 'act.searched',
  clipped: 'act.clipped',
  adjustedClip: 'act.adjustedClip',
  quoted: 'act.quoted',
  addedFile: 'act.addedFile',
  viewedItem: 'act.viewedItem',
  posted: 'act.posted',
  other: 'act.other',
}

/** The status line's words for the change being made right now, or null. */
export function actionStatus(
  actions: Action[],
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
): string | null {
  const v = viewActions(actions)
    .filter((x) => x.running)
    .at(-1)
  if (!v) return null
  if (v.intent) return `${v.intent}…`
  return `${t(VERB[v.verb], { n: v.count })}${v.hint ? `「${v.hint}」` : ''}…`
}
